// views/Admin/Statistics/components/Charts/BeaconCharts/QrScans/QrScansByHoursChart/QrScansByHoursChart.tsx
import { useState, useMemo, useCallback, useEffect, type MouseEvent, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import styles from './QrScansByHoursChart.module.css';

// Approx rendered width of the tooltip (matches the CSS min/max-width band).
// Used to keep it inside the viewport on narrow screens instead of clipping.
const TOOLTIP_WIDTH = 220;
const TOOLTIP_MARGIN = 10;
const TOOLTIP_FLIP_THRESHOLD = 160;

function computeTooltipPosition(clientX: number, clientY: number) {
  const half = TOOLTIP_WIDTH / 2;
  const minX = half + TOOLTIP_MARGIN;
  const maxX = Math.max(minX, window.innerWidth - half - TOOLTIP_MARGIN);
  const clampedClientX = Math.min(Math.max(clientX, minX), maxX);
  // If there isn't room above the touch/cursor point (near the top of the
  // viewport, common once the page is scrolled on mobile), flip the
  // tooltip to render below instead of above.
  const flip = clientY < TOOLTIP_FLIP_THRESHOLD;
  return {
    x: clampedClientX + window.scrollX,
    y: clientY + window.scrollY,
    flip,
  };
}

const PALETTE = [
  '#4f6ef7', '#f75f6e', '#f7a84f', '#4fd6f7', '#a84ff7',
  '#4ff7a0', '#f74fc8', '#f7e24f', '#4fb3f7', '#f76b4f',
  '#7af74f', '#f74f8e', '#4ff7d6', '#c8f74f', '#7b4ff7',
  '#f7c44f', '#4f9ef7', '#f74f4f', '#4ff77a', '#d64ff7',
  '#f79e4f', '#4f6ef7', '#f74fb3', '#b3f74f', '#4fe8f7',
  '#f76ef7', '#6ef74f', '#f74f6e', '#4f4ff7',
];

type ColorMap = Record<string, string>;
interface Segment { device_id: string; count: number; color: string; }
interface HourData { hour: number; total: number; segments: Segment[]; }

function buildColorMap(tableData: any): ColorMap {
  const seen = new Set<string>();
  const map: ColorMap = {};
  tableData.device_id.forEach((id: string) => {
    if (!seen.has(id)) {
      map[id] = PALETTE[seen.size] ?? '#888';
      seen.add(id);
    }
  });
  return map;
}

function toLocalDateKey(ts: number): string {
  const d = new Date(ts * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function extractDays(tableData: any): string[] {
  const days = new Set<string>();
  tableData.timestamp.forEach((t: number) => days.add(toLocalDateKey(t)));
  return [...days].sort();
}

function buildHourlyData(tableData: any, colorMap: ColorMap, selectedDay: string | null) {
  const hourMap: Record<number, Record<string, number>> = {};
  const deviceTotals: Record<string, number> = {};

  tableData.timestamp.forEach((t: number, i: number) => {
    const day = toLocalDateKey(t);
    if (day !== selectedDay) return;
    const hour = new Date(t * 1000).getHours();
    const dev = tableData.device_id[i];
    if (!hourMap[hour]) hourMap[hour] = {};
    hourMap[hour][dev] = (hourMap[hour][dev] || 0) + 1;
    deviceTotals[dev] = (deviceTotals[dev] || 0) + 1;
  });

  const hours: HourData[] = Array.from({ length: 24 }, (_, h) => {
    const devMap = hourMap[h] || {};
    const segments: Segment[] = Object.entries(devMap)
      .map(([device_id, count]) => ({ device_id, count, color: colorMap[device_id] }))
      .sort((a, b) => b.count - a.count);
    return { hour: h, total: segments.reduce((s, x) => s + x.count, 0), segments };
  });

  const maxHourTotal = Math.max(...hours.map((h) => h.total), 1);

  const sortedDeviceTotals: Segment[] = Object.entries(deviceTotals)
    .map(([device_id, count]) => ({ device_id, count, color: colorMap[device_id] }))
    .sort((a, b) => b.count - a.count);

  return { hours, maxHourTotal, deviceTotals: sortedDeviceTotals };
}

const pad = (n: number) => String(n).padStart(2, '0');

interface TooltipState { x: number; y: number; flip: boolean; activeHour: HourData; }

const QrScansByHoursChart = ({ table }: { table: any }) => {
  const colorMap = useMemo(() => buildColorMap(table), [table]);
  const availableDays = useMemo(() => extractDays(table), [table]);
  const [selectedDay, setSelectedDay] = useState<string | null>(availableDays[availableDays.length - 1] ?? null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [hoveredDevice, setHoveredDevice] = useState<string | null>(null);

  const { hours, maxHourTotal, deviceTotals } = useMemo(
    () => buildHourlyData(table, colorMap, selectedDay),
    [table, colorMap, selectedDay],
  );

  const dayLabel = useMemo(() => {
    if (!selectedDay) return '';
    return new Date(`${selectedDay}T12:00:00Z`).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
    });
  }, [selectedDay]);

  const dayTotal = useMemo(() => hours.reduce((s, h) => s + h.total, 0), [hours]);

  const handleBarEnter = useCallback((e: MouseEvent, activeHour: HourData) => {
    const { x, y, flip } = computeTooltipPosition(e.clientX, e.clientY);
    setTooltip({ x, y, flip, activeHour });
  }, []);
  const handleBarMove = useCallback((e: MouseEvent) => {
    setTooltip((prev) => {
      if (!prev) return null;
      const { x, y, flip } = computeTooltipPosition(e.clientX, e.clientY);
      return { ...prev, x, y, flip };
    });
  }, []);
  const handleBarLeave = useCallback(() => setTooltip(null), []);

  // Touch devices don't fire hover events — tap to show/move, tap again to dismiss.
  const handleBarTouch = useCallback((e: TouchEvent, activeHour: HourData) => {
    const touch = e.touches[0] ?? e.changedTouches[0];
    if (!touch) return;
    const { x, y, flip } = computeTooltipPosition(touch.clientX, touch.clientY);
    setTooltip((prev) => (prev && prev.activeHour.hour === activeHour.hour ? null : { x, y, flip, activeHour }));
  }, []);

  useEffect(() => {
    if (!tooltip) return;
    const dismiss = (e: globalThis.TouchEvent) => {
      if (!(e.target as HTMLElement)?.closest(`.${styles.stackWrapper}`)) setTooltip(null);
    };
    document.addEventListener('touchstart', dismiss);
    return () => document.removeEventListener('touchstart', dismiss);
  }, [tooltip]);

  const yTicks = useMemo(() => {
    const step = maxHourTotal <= 5 ? 1 : maxHourTotal <= 10 ? 2 : maxHourTotal <= 25 ? 5 : 10;
    const ticks: number[] = [];
    for (let v = 0; v <= maxHourTotal; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] < maxHourTotal) ticks.push(maxHourTotal);
    return ticks;
  }, [maxHourTotal]);

  const CHART_H = 280;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Device activity · hourly breakdown</p>
          <h2 className={styles.title}>Timeline View</h2>
        </div>
        <div className={styles.meta}>
          <span className={styles.metaLabel}>Events on day</span>
          <span className={styles.metaValue}>{dayTotal}</span>
        </div>
      </div>

      <div className={styles.daySelector}>
        <span className={styles.daySelectorLabel}>Select day</span>
        <div className={styles.dayPills}>
          {availableDays.map((day) => {
            const label = new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
            return (
              <button key={day} className={`${styles.pill} ${day === selectedDay ? styles.pillActive : ''}`} onClick={() => setSelectedDay(day)}>
                {label}
              </button>
            );
          })}
        </div>
        <span className={styles.dayFull}>{dayLabel}</span>
      </div>

      <div className={styles.chartArea}>
        <div className={styles.yAxis}>
          {yTicks.map((v) => (
            <span key={v} className={styles.yTick}>{v}</span>
          ))}
        </div>

        <div className={styles.chartInner}>
          <div className={styles.scrollArea}>
            <div className={styles.chartContent}>
              <div className={styles.gridLines} style={{ height: CHART_H }}>
                {yTicks.map((v) => (
                  <div key={v} className={styles.gridLine} style={{ bottom: `${(v / maxHourTotal) * 100}%` }} />
                ))}
              </div>

              <div className={styles.barsRow}>
                {hours.map((h) => (
                  <div key={h.hour} className={styles.hourColumn}>
                    <div className={styles.barZone} style={{ height: CHART_H }}>
                      <div
                        className={styles.stackWrapper}
                        onMouseEnter={(e) => handleBarEnter(e, h)}
                        onMouseMove={handleBarMove}
                        onMouseLeave={handleBarLeave}
                        onTouchStart={(e) => handleBarTouch(e, h)}
                      >
                        {h.segments.map((seg) => (
                          <div
                            key={seg.device_id}
                            className={styles.segment}
                            style={{
                              height: `${(seg.count / maxHourTotal) * CHART_H}px`,
                              backgroundColor: seg.color,
                              opacity: hoveredDevice && hoveredDevice !== seg.device_id ? 0.25 : 1,
                            }}
                          />
                        ))}
                        {h.total === 0 && <div className={styles.emptyBar} />}
                      </div>
                    </div>
                    <div className={styles.hourLabel}>
                      {pad(h.hour)}
                      <span className={styles.hourSuffix}>h</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.devicePanel}>
        <div className={styles.devicePanelHeader}>
          <span className={styles.devicePanelTitle}>Device frequencies</span>
          <span className={styles.devicePanelSub}>{deviceTotals.length} active devices</span>
        </div>
        <div className={styles.deviceList}>
          {deviceTotals.map(({ device_id, count, color }) => {
            const pctVal = ((count / dayTotal) * 100).toFixed(1);
            return (
              <div
                key={device_id}
                className={`${styles.deviceRow} ${hoveredDevice === device_id ? styles.deviceRowHovered : ''}`}
                onMouseEnter={() => setHoveredDevice(device_id)}
                onMouseLeave={() => setHoveredDevice(null)}
              >
                <div className={styles.deviceSwatch} style={{ backgroundColor: color }} />
                <span className={styles.deviceId}>{device_id}</span>
                <div className={styles.deviceBarTrack}>
                  <div className={styles.deviceBarFill} style={{ width: `${pctVal}%`, backgroundColor: color }} />
                </div>
                <span className={styles.deviceCount}>{count}</span>
                <span className={styles.devicePct}>{pctVal}%</span>
              </div>
            );
          })}
          {deviceTotals.length === 0 && <p className={styles.noData}>No activity on this day.</p>}
        </div>
      </div>

      {tooltip &&
        tooltip.activeHour.total > 0 &&
        createPortal(
          <div
            className={styles.tooltip}
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: tooltip.flip ? 'translate(-50%, 12px)' : 'translate(-50%, calc(-100% - 12px))',
            }}
          >
            <div className={styles.tooltipTime}>
              {pad(tooltip.activeHour.hour)}:00 – {pad(tooltip.activeHour.hour + 1)}:00
            </div>
            <div className={styles.tooltipTotal}>{tooltip.activeHour.total} events</div>
            <div className={styles.tooltipRows}>
              {tooltip.activeHour.segments.map((seg) => (
                <div key={seg.device_id} className={styles.tooltipRow}>
                  <div className={styles.tooltipSwatch} style={{ backgroundColor: seg.color }} />
                  <span className={styles.tooltipDeviceId}>{seg.device_id}</span>
                  <span className={styles.tooltipCount}>{seg.count}</span>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default QrScansByHoursChart;